import { fromZonedTime } from "date-fns-tz";
import { format } from "date-fns";

export function toUnixTimestamp(
  date: string | Date,
  timezone: string | undefined = "UTC",
): number {
  const dateWithTimezone = fromZonedTime(date, timezone);
  return Math.floor(dateWithTimezone.getTime() / 1000);
}

export function toISOString(
  value: string | Date | null | undefined,
): string | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function normalizeDateKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function getDefaultTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function getTimezones(): string[] {
  return Intl.supportedValuesOf("timeZone");
}

/**
 * Converts a UTC datetime string to datetime-local format (YYYY-MM-DDTHH:mm)
 * This is used for datetime-local input fields which expect local time values
 */
export function toDatetimeLocal(datetime: string | Date): string {
  const date = typeof datetime === "string" ? new Date(datetime) : datetime;
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

/**
 * Formats a date/time with timezone using next-intl's formatter.
 * Server-side utility for formatting dates in server components.
 *
 * @param dateTime - ISO date string or Date object
 * @param timezone - The timezone to display the date in (e.g., "America/New_York")
 * @param dateStyle - The date style to use (default: "medium")
 * @param timeStyle - The time style to use (default: "short")
 * @returns Formatted date string
 */
export async function formatDateTime(
  dateTime: string | Date,
  timezone: string | undefined = "UTC",
  dateStyle: Intl.DateTimeFormatOptions["dateStyle"] | "none" = "medium",
  timeStyle: Intl.DateTimeFormatOptions["timeStyle"] | "none" = "short",
): Promise<string> {
  const { getFormatter } = await import("next-intl/server");
  const format = await getFormatter();
  const date = typeof dateTime === "string" ? new Date(dateTime) : dateTime;

  return format.dateTime(date, {
    dateStyle: dateStyle === "none" ? undefined : dateStyle,
    timeStyle: timeStyle === "none" ? undefined : timeStyle,
    timeZone: timezone,
  });
}
